import goodsIntakeFixtures from '../../fixtures/goods-intake.json';
import {
  acceptedQuantity,
  isOpenReceipt,
  lineDiscrepancies,
  normalizeReceiptCode,
  postBlocker,
  validateReceiptInput,
  type ExpectedReceipt,
  type ExpectedReceiptInput,
  type ExpectedReceiptLine,
  type PostReceiptResult,
  type ReceiptCount,
} from '../domain/goods-intake';
import { expectedReceiptSchema } from '../domain/validation';
import { ConflictError, FulfillmentError, NotFoundError, ValidationError } from './errors';
import type { GoodsIntakeApi, ReceiptFilters } from './goods-intake-api';
import { stockApi } from './mock-fulfillment-api';
import type { StockApi } from './stock-api';

const MOCK_USER = 'mock.current-user@example.test';
const DAY = 86_400_000;

const wait = async (milliseconds = 80): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clone = <T>(value: T): T => structuredClone(value);

let counter = 0;
const mockId = (prefix: string): string => `${prefix}-${Date.now()}-${++counter}`;

function reject(reason: 'INVALID_STATE' | 'INACTIVE' | 'UNKNOWN_REFERENCE', detail: string): never {
  throw new FulfillmentError(`${reason}: ${detail}`, reason);
}

type FixtureReceipt = Record<string, unknown> & { expectedInDays?: number };

/**
 * Deterministic Goods Intake mirroring leatherback's rules (#8100): the
 * manifest is fixed once counting starts, posting needs every line counted
 * and puts received minus damaged into stock, all or nothing.
 */
export class MockGoodsIntakeApi implements GoodsIntakeApi {
  private receipts: ExpectedReceipt[];

  constructor(private readonly stock: StockApi) {
    const now = Date.now();
    this.receipts = (goodsIntakeFixtures.receipts as FixtureReceipt[]).map((raw) => {
      const { expectedInDays, ...rest } = raw;
      const expectedAt =
        expectedInDays === undefined ? undefined : new Date(now + expectedInDays * DAY);
      expectedAt?.setUTCHours(9, 0, 0, 0);
      return expectedReceiptSchema.parse({
        ...rest,
        expectedAt: expectedAt?.toISOString(),
        postedAt: rest.state === 'POSTED' ? expectedAt?.toISOString() : undefined,
        createTime: new Date(now - 10 * DAY).toISOString(),
        createdBy: 'lager@tidalx.ai',
        version: rest.state === 'EXPECTED' ? 0 : 2,
      });
    });
  }

  private receipt(id: string): ExpectedReceipt {
    const receipt = this.receipts.find((candidate) => candidate.id === id);
    if (!receipt) throw new NotFoundError('expected receipt not found');
    return receipt;
  }

  private checkVersion(receipt: ExpectedReceipt, version: number): void {
    if (receipt.version !== version) {
      throw new ConflictError('expected receipt was changed by someone else; reload and try again');
    }
  }

  private touch(receipt: ExpectedReceipt): ExpectedReceipt {
    receipt.version += 1;
    receipt.lastModified = new Date().toISOString();
    return clone(receipt);
  }

  private async checkItem(stockItemId: string): Promise<void> {
    const item = (await this.stock.listStockItems(true)).find((s) => s.id === stockItemId);
    if (!item) reject('UNKNOWN_REFERENCE', `stock item ${stockItemId}`);
    if (!item.active) reject('INACTIVE', `stock item ${item.sku}`);
  }

  private async checkLocation(locationId: string, siteId: string): Promise<void> {
    const location = (await this.stock.listWarehouseLocations(undefined, true)).find(
      (l) => l.id === locationId,
    );
    if (!location) reject('UNKNOWN_REFERENCE', `location ${locationId}`);
    if (location.siteId !== siteId)
      reject('UNKNOWN_REFERENCE', `location ${location.code} is at another site`);
    if (!location.active) reject('INACTIVE', `location ${location.code}`);
  }

  async listReceipts(filters?: ReceiptFilters): Promise<ExpectedReceipt[]> {
    await wait();
    return clone(
      this.receipts.filter(
        (r) =>
          (!filters?.siteId || r.siteId === filters.siteId) &&
          (!filters?.openOnly || isOpenReceipt(r)),
      ),
    );
  }

  async getReceipt(id: string): Promise<ExpectedReceipt> {
    await wait();
    return clone(this.receipt(id));
  }

  async findReceipts(code: string): Promise<ExpectedReceipt[]> {
    await wait();
    const needle = normalizeReceiptCode(code);
    if (!needle) throw new ValidationError('code must contain letters or digits');
    return clone(
      this.receipts.filter(
        (r) =>
          isOpenReceipt(r) &&
          (r.identifiers.includes(needle) ||
            normalizeReceiptCode(r.supplierReference ?? '') === needle),
      ),
    );
  }

  async createReceipt(input: ExpectedReceiptInput): Promise<ExpectedReceipt> {
    await wait();
    const problem = validateReceiptInput(input);
    if (problem) throw new ValidationError(problem);
    for (const line of input.lines) await this.checkItem(line.stockItemId);
    const now = new Date().toISOString();
    const receipt: ExpectedReceipt = {
      id: mockId('receipt'),
      siteId: input.siteId,
      supplier: input.supplier?.trim() || undefined,
      supplierReference: input.supplierReference?.trim() || undefined,
      identifiers: [...new Set(input.identifiers.map(normalizeReceiptCode).filter(Boolean))],
      source: 'MANUAL',
      expectedAt: input.expectedAt,
      notes: input.notes?.trim() || undefined,
      lines: input.lines.map((line) => ({
        id: mockId('line'),
        stockItemId: line.stockItemId,
        expectedQuantity: line.expectedQuantity,
      })),
      state: 'EXPECTED',
      discrepancies: [],
      createdBy: MOCK_USER,
      createTime: now,
      version: 0,
      lastModified: now,
    };
    this.receipts.push(receipt);
    return clone(receipt);
  }

  async recordIntake(
    id: string,
    version: number,
    counts: ReceiptCount[],
  ): Promise<ExpectedReceipt> {
    await wait();
    const receipt = this.receipt(id);
    this.checkVersion(receipt, version);
    if (!isOpenReceipt(receipt))
      reject('INVALID_STATE', `a ${receipt.state} receipt can't be counted`);
    // Check everything before changing anything.
    const next = clone(receipt.lines);
    for (const count of counts) {
      if (count.damagedQuantity > count.receivedQuantity) {
        throw new ValidationError('damaged can’t exceed received');
      }
      let line: ExpectedReceiptLine | undefined = count.lineId
        ? next.find((l) => l.id === count.lineId)
        : next.find((l) => l.stockItemId === count.stockItemId);
      if (!line && count.lineId) reject('UNKNOWN_REFERENCE', `line ${count.lineId}`);
      if (!line) {
        await this.checkItem(count.stockItemId ?? '');
        line = { id: mockId('line'), stockItemId: count.stockItemId ?? '', expectedQuantity: 0 };
        next.push(line);
      }
      if (count.locationId) {
        await this.checkLocation(count.locationId, receipt.siteId);
        line.locationId = count.locationId;
      }
      line.receivedQuantity = count.receivedQuantity;
      line.damagedQuantity = count.damagedQuantity;
      if (count.note !== undefined) line.note = count.note.trim() || undefined;
    }
    receipt.lines = next;
    receipt.state = 'RECEIVING';
    return this.touch(receipt);
  }

  async postReceipt(id: string, version: number): Promise<PostReceiptResult> {
    await wait();
    const receipt = this.receipt(id);
    if (receipt.state === 'POSTED') return { receipt: clone(receipt), replayed: true };
    this.checkVersion(receipt, version);
    const blocker = postBlocker(receipt);
    if (blocker) reject('INVALID_STATE', blocker);
    // All or nothing: every location and item is checked before any stock moves.
    for (const line of receipt.lines) {
      if (acceptedQuantity(line) === 0) continue;
      await this.checkItem(line.stockItemId);
      await this.checkLocation(line.locationId ?? '', receipt.siteId);
    }
    for (const line of receipt.lines) {
      const accepted = acceptedQuantity(line);
      if (accepted === 0) continue;
      const result = await this.stock.recordStockCommand({
        kind: 'receive',
        stockItemId: line.stockItemId,
        locationId: line.locationId ?? '',
        quantity: accepted,
        referenceType: 'EXPECTED_RECEIPT',
        referenceId: receipt.id,
        note: [receipt.supplier, receipt.supplierReference].filter(Boolean).join(' '),
        idempotencyKey: `expected-receipt|${receipt.id}|${line.id}`,
      });
      line.movementId = result.movement.id;
    }
    receipt.discrepancies = receipt.lines.flatMap((line) =>
      lineDiscrepancies(line).map((d) => ({
        lineId: line.id,
        stockItemId: line.stockItemId,
        kind: d.kind,
        quantity: d.quantity,
        note: line.note,
      })),
    );
    receipt.state = 'POSTED';
    receipt.postedBy = MOCK_USER;
    receipt.postedAt = new Date().toISOString();
    return { receipt: this.touch(receipt), replayed: false };
  }

  async cancelReceipt(id: string, version: number, reason: string): Promise<ExpectedReceipt> {
    await wait();
    const receipt = this.receipt(id);
    this.checkVersion(receipt, version);
    if (!isOpenReceipt(receipt))
      reject('INVALID_STATE', `a ${receipt.state} receipt can't be cancelled`);
    if (!reason.trim()) throw new ValidationError('reason must be set');
    receipt.state = 'CANCELLED';
    receipt.cancelReason = reason.trim();
    return this.touch(receipt);
  }
}

export const goodsIntakeApi: GoodsIntakeApi = new MockGoodsIntakeApi(stockApi);
